import React from "react";
import { useTranslation } from "react-i18next";
import { Contact } from "../types/User";

interface ContactSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  contacts: Contact[];
  contactType: Contact["type"];
  onSelect: (contact: Contact) => void;
}

const getContactIcon = (type: Contact["type"]): string => {
  const iconMap: Record<Contact["type"], string> = {
    email: "/icons/socials/email.svg",
    instagram: "/icons/socials/instagram.svg",
    x: "/icons/socials/x.svg",
    linkedin: "/icons/socials/linkedin.svg",
    facebook: "/icons/socials/facebook.svg",
    website: "/icons/socials/website.svg",
    telegram: "/icons/socials/telegram.svg",
  };
  return iconMap[type];
};

const getContactTypeLabel = (
  type: Contact["type"],
  t: (key: string) => string
): string => {
  const labelMap: Record<Contact["type"], string> = {
    email: t("pages.profile.contactTypes.email", "Email"),
    instagram: t("pages.profile.contactTypes.instagram", "Instagram"),
    x: t("pages.profile.contactTypes.x", "X (Twitter)"),
    linkedin: t("pages.profile.contactTypes.linkedin", "LinkedIn"),
    facebook: t("pages.profile.contactTypes.facebook", "Facebook"),
    website: t("pages.profile.contactTypes.website", "Sito Web"),
    telegram: t("pages.profile.contactTypes.telegram", "Telegram"),
  };
  return labelMap[type];
};

const ContactSelectorModal: React.FC<ContactSelectorModalProps> = ({
  isOpen,
  onClose,
  contacts,
  contactType,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleSelect = (contact: Contact) => {
    onSelect(contact);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#1F1235] border-modal shadow-[0_30px_60px_rgba(3,0,28,0.55)]">
        <div className="px-6 pt-6 pb-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-white">
                {t("pages.profile.selectContact", "Seleziona Contatto")}
              </h2>
              <p className="text-white/70 text-sm mt-1">
                {t(
                  "pages.profile.selectContactSubtitle",
                  "Scegli quale {{type}} vuoi aprire",
                  { type: getContactTypeLabel(contactType, t) }
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="flex flex-col gap-2 mt-4">
            {contacts.map((contact, index) => (
              <button
                key={`${contact.value}-${index}`}
                onClick={() => handleSelect(contact)}
                className="flex items-center gap-3 rounded-xl bg-[#2C1A4A] border border-white/10 px-4 py-3 text-left hover:bg-[#35205B] transition-colors"
              >
                <img
                  src={getContactIcon(contact.type)}
                  alt={contact.type}
                  className="w-6 h-6 flex-shrink-0"
                />
                <span className="text-white text-sm flex-1 truncate">
                  {contact.value}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSelectorModal;
